import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as amplitude from "@amplitude/analytics-browser";

import {
  CircleExclamation
} from "@unified-checkout/design-system/icons/index";
import {
  PageStatus,
  Breadcrumbs,
  SkeletonBase
} from "@unified-checkout/design-system";

import {
  CHECKOUT_CUSTOMIZATION_EVENTS,
  CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS
} from "@app/dashboard/constants/tracking_events/checkout-customization";
import { SHARED_EVENTS_PARAMS } from "@app/dashboard/constants/tracking_events/shared";
import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { MOBILE_BREAKPOINT } from "@app/dashboard/constants/break-points";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import {
  INITIAL_STATE as CUSTOMIZATION_INITIAL_STATE,
  useCheckoutCustomizationStore
} from "@app/dashboard/store/checkout-customization";
import { useScreenWidth } from "@app/dashboard/hooks/useScreenWidth";
import { getCustomizationListing } from "@app/dashboard/services/inner_layout/checkout_customization/listing";
import type { TNavTabs } from "@app/dashboard/types/innerLayout/checkout_customization";
import { getCurrentMode } from "@app/dashboard/utils/getCurrentMode";
import { useSettingsUtils } from "../Settings/useSettingsUtils";
import { CustomizationNotAvailable } from "./components/NotAvailable";
import { Sidebar as CheckoutCustomizationSidebar } from "./Sidebar";
import { Preview } from "./Preview";
import { ActionsBox } from "./ActionsBox";
import {
  filterToMatchStructure,
  NAVIGATION_TABS,
  TAB_TO_EVENT_VALUE_MAP
} from "./utils";
import styles from "./checkout-customization.module.scss";


export const CheckoutCustomization = () => {
  const { t } = useTranslation();

  const { isLive } = useTestLiveModeStore();
  const { internalPagesBreadcrumbs } = useSettingsUtils();
  const screenWidth = useScreenWidth();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const {
    errors,
    setCustomizationData,
    setBusinessLogo,
    setIsFetched,
    initialData,
    setInitialData,
    customizationData,
    businessLogo
  } = useCheckoutCustomizationStore();

  const [activeTab, setActiveTab] = useState<TNavTabs>(NAVIGATION_TABS.BRANDING);
  const isInitialRenderRef = useRef(true);

  const {
    isLoading,
    isFetched,
    isError,
    error,
    data,
  } = useQuery({
    queryKey: [QUERY_KEYS.CUSTOMIZATION_LISTING /* ,isLive */],
    queryFn: () => getCustomizationListing(false).then(res => res.data),
    enabled: !isMobile
  });

  const err = error as any;
  const errMsg = err?.response?.data?.message || err?.response?.data?.detail;

  useEffect(() => {
    amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.PAGE_VIEWED, {
      [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive)
    });
  }, []);

  useEffect(() => {
    if (!isInitialRenderRef.current) {
      amplitude.track(CHECKOUT_CUSTOMIZATION_EVENTS.TAB_VIEWED, {
        [CHECKOUT_CUSTOMIZATION_EVENTS_PARAMS.TAB_NAME]: TAB_TO_EVENT_VALUE_MAP[activeTab]
      });
    } else {
      isInitialRenderRef.current = false;
    }
  }, [activeTab]);

  useEffect(() => {
    if (data) {
      const { customization_data, merchant_logo } = data;

      const filteredCustomizationData = filterToMatchStructure(CUSTOMIZATION_INITIAL_STATE, customization_data);
      setCustomizationData(filteredCustomizationData);

      setBusinessLogo({
        ...businessLogo,
        path: merchant_logo
      });
      setInitialData({
        customization_data: filteredCustomizationData,
        merchant_logo: merchant_logo
      });
    }

  }, [data]);

  useEffect(() => {
    setIsFetched(isFetched);
  }, [isFetched]);

  return (
    <div className={styles["checkout-customization-wrapper"]}>
      {
        isLoading ?
          <div className={styles["checkout-customization-wrapper__loading-state"]}>
            <SkeletonBase width="16rem" height="1.5rem" />
            <SkeletonBase width="20rem" height="2rem" />
            <SkeletonBase width="100%" height="100%" />
          </div>
          :
          isError ?
            <div className={styles["checkout-customization-wrapper__error-state"]}>
              <PageStatus
                status="error"
                title={errMsg || t('innerLayout.errors.smthWentWrong')}
                description={errMsg ? '' : t('innerLayout.errors.dataRetrievalErr')}
                icon={CircleExclamation}
              />
            </div>
            :
            <>
              {
                !isMobile &&
                <Breadcrumbs
                  linksProps={internalPagesBreadcrumbs['CHECKOUT_CUSTOMIZATION']}
                />
              }

              <div className={styles["checkout-customization-wrapper__title-box"]}>
                <div className={styles["checkout-customization-wrapper__title-box__title"]}>
                  {t('innerLayout.checkoutCustomizationPage.title')}
                </div>

                <div className={styles["checkout-customization-wrapper__title-box__sub-title"]}>
                  {t('innerLayout.checkoutCustomizationPage.subTitle')}
                </div>
              </div>


              {
                isMobile ?
                  <CustomizationNotAvailable />
                  :
                  <>
                    <div className={styles["checkout-customization-wrapper__main"]}>
                      <CheckoutCustomizationSidebar
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                      />
                      <Preview activeTab={activeTab} />
                    </div>

                    <div className={styles["checkout-customization-wrapper__actions-box"]}>
                      <ActionsBox
                        activeTab={activeTab}
                      />
                    </div>
                  </>
              }
            </>
      }
    </div>
  )
}